import { createAsyncThunk } from "@reduxjs/toolkit";
import { ACTIONS_NOTIFICATIONS } from "../../action-types/actions";
import { APP_PATH } from "../../api/backendPath";
import axios from "axios";
import { toast } from "../../components/app-toast";
import { handleAccountDeactivated } from "../../utils/accountDeactivated";

import i18n from "../../i18n";
// Create axios instance with auth token
const createAxiosInstance = () => {
  const token = sessionStorage.getItem("usr_1a2b3c");
  const instance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  // Account deactivated by an admin → show the block modal (once). The gateway
  // returns 403 with error_code "ACCOUNT_DEACTIVATED" for every request once
  // the account is blocked.
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      handleAccountDeactivated(error);
      return Promise.reject(error);
    },
  );

  return instance;
};

// Get user notifications with pagination and filtering
export const getUserNotifications = createAsyncThunk(
  ACTIONS_NOTIFICATIONS.get_user_notifications,
  async (
    { page = 1, limit = 20, status, notification_type } = {},
    { rejectWithValue },
  ) => {
    try {
      const axiosInstance = createAxiosInstance();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (status) params.append("status", status);
      if (notification_type)
        params.append("notification_type", notification_type);

      const response = await axiosInstance.get(
        `${APP_PATH.getUserNotifications}?${params.toString()}`,
      );

      // Handle different response structures
      if (response.data?.data) {
        return response.data.data;
      } else if (response.data?.notifications) {
        // Fallback for different response structure
        return response.data;
      } else {
        console.warn(
          "getUserNotifications: Unexpected response structure, returning empty data",
        );
        return {
          notifications: [],
          pagination: { total: 0, page: 1, limit: 20, totalPages: 0 },
        };
      }
    } catch (error) {
      console.error("Get notifications error:", error);
      const errorMessage =
        error?.response?.data?.message || i18n.t("toast.failedToFetchNotifications");
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// Mark notification as read
export const markNotificationRead = createAsyncThunk(
  ACTIONS_NOTIFICATIONS.mark_notification_read,
  async (notificationId, { rejectWithValue }) => {
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.put(
        `${APP_PATH.markNotificationRead}/${notificationId}/read`,
      );

      // No success toast on read — only surface errors (handled in catch)
      return response.data.data;
    } catch (error) {
      console.error("Mark notification read error:", error);
      const errorMessage =
        error?.response?.data?.message || i18n.t("toast.failedToMarkNotificationAsRead");
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// Delete notification
export const deleteNotification = createAsyncThunk(
  ACTIONS_NOTIFICATIONS.delete_notification,
  async (notificationId, { rejectWithValue }) => {
    try {
      const axiosInstance = createAxiosInstance();
      await axiosInstance.delete(
        `${APP_PATH.deleteNotification}/${notificationId}`,
      );

      // No success toast - silent deletion
      return notificationId;
    } catch (error) {
      console.error("Delete notification error:", error);
      const errorMessage =
        error?.response?.data?.message || i18n.t("toast.failedToDeleteNotification");
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// Get unread notification count
export const getUnreadNotificationCount = createAsyncThunk(
  ACTIONS_NOTIFICATIONS.get_unread_notification_count,
  async (_, { rejectWithValue }) => {
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        APP_PATH.getUnreadNotificationCount,
      );

      console.log("getUnreadNotificationCount API response:", response);
      console.log("getUnreadNotificationCount response.data:", response.data);

      // Handle different response structures
      if (response.data?.data) {
        return response.data.data;
      } else if (response.data?.unread_count !== undefined) {
        // Fallback for different response structure
        return response.data;
      } else {
        console.warn(
          "getUnreadNotificationCount: Unexpected response structure, returning 0",
        );
        return { unread_count: 0 };
      }
    } catch (error) {
      console.error("Get unread count error:", error);
      const errorMessage =
        error?.response?.data?.message || i18n.t("toast.failedToGetUnreadCount");
      return rejectWithValue(errorMessage);
    }
  },
);

// Mark all notifications as read
export const markAllNotificationsRead = createAsyncThunk(
  ACTIONS_NOTIFICATIONS.mark_all_notifications_read,
  async (_, { rejectWithValue }) => {
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.put(
        APP_PATH.markAllNotificationsRead,
      );

      return response.data;
    } catch (error) {
      console.error("Mark all notifications read error:", error);
      const errorMessage =
        error?.response?.data?.message ||
        i18n.t("toast.failedToMarkAllNotificationsAsRead");
      return rejectWithValue(errorMessage);
    }
  },
);
